import React from 'react';
import { Card, Empty, Badge, Button } from 'antd';
import { GiftOutlined, TrophyOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import './style.scss';

const RecentActivityCard = ({
  recentActivities = [],
  onClaimGift,
  onViewRecognition,
  onMarkAsSeen,
  disableSpotlight = false,
  isLoading = false
}) => {
  const getActivityIcon = (type, status) => {
    switch (type) {
      case 'badge_assignment':
        return <TrophyOutlined style={{ color: '#E75DA7' }} />;
      case 'gift_received':
        return <GiftOutlined style={{ color: '#E75DA7' }} />;
      case 'gift_redeemed':
        return <CheckCircleOutlined style={{ color: '#1890ff' }} />;
      case 'gift_expired':
        return <ClockCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <TrophyOutlined style={{ color: '#E75DA7' }} />;
    }
  };

  const getActivityTitle = (activity) => {
    switch (activity.type) {
      case 'badge_assignment':
        return `New Recognition: ${activity.badgeName}`;
      case 'gift_received':
        return `Gift from "${activity.badgeName || activity.title || 'Recognition'}"`;
      case 'gift_redeemed':
        return `Gift from "${activity.badgeName || activity.title || 'Recognition'}" Redeemed`;
      case 'gift_expired':
        return `Gift from "${activity.badgeName || activity.title || 'Recognition'}" Expired`;
      default:
        return 'Activity Update';
    }
  };

  const getActivityDescription = (activity) => {
    switch (activity.type) {
      case 'badge_assignment':
        return activity.sentWithoutSnappyGrant
          ? 'Recognition badge earned'
          : 'Recognition with gift attached';
      case 'gift_received':
        return 'Click to claim your gift from Snappy';
      case 'gift_redeemed':
        return 'You have successfully redeemed your gift';
      case 'gift_expired':
        return 'This gift is no longer available';
      default:
        return activity.description || '';
    }
  };

  const renderActivityAction = (activity) => {
    switch (activity.type) {
      case 'badge_assignment':
        if (!activity.sentWithoutSnappyGrant && activity.status === 'unviewed') {
          return (
            <Button 
              type="primary" 
              size="small"
              onClick={() => onViewRecognition?.(activity)}
            >
              View Recognition
            </Button>
          );
        }
        return null;
      case 'gift_received':
        return (
          <motion.div
            initial={false}
            animate={activity.shouldShake && !disableSpotlight ? {
              x: [-2, 2, -2, 2, 0],
              rotate: [-1, 1, -1, 1, 0]
            } : {}}
            transition={{
              duration: 0.5,
              repeat: activity.shouldShake && !disableSpotlight ? Infinity : 0,
              repeatType: "reverse",
              repeatDelay: 3.0, // 3 seconds between shake cycles
              ease: "easeInOut",
              delay: activity.shouldShake && !disableSpotlight ? (activity.shakeIndex || 0) * 0.3 : 0
            }}
            style={{ display: 'inline-block' }} // Ensure proper display
          >
            <Button
              type="link"
              size="large"
              icon={<GiftOutlined />}
              onClick={() => onClaimGift?.(activity)}
              style={{
                fontSize: '15px',
                color: '#6294FF',
                pointerEvents: 'auto' // Ensure button is clickable
              }}
            >
              <strong>Claim Gift</strong>
            </Button>
          </motion.div>
        );
      default:
        return null;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return activityTime.toLocaleDateString();
  };

  // Filter to only show gift claim events
  const claimActivities = recentActivities.filter(activity => activity.type === 'gift_received');

  return (
    <div className="recent-activity-card">
      {claimActivities.filter(a => !a.hasSeen).length > 0 && (
        <div className="recent-activity-header">
          <p></p>
          <Badge count={claimActivities.filter(a => !a.hasSeen).length} />
        </div>
      )}
      
      <div className="recent-activity-content">
        {claimActivities.length === 0 ? (
          <Empty
            description="All Caught Up!"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="activity-list">
            {claimActivities.map((activity, index) => (
              <Card
                key={activity.id || index}
                className={`activity-item ${activity.isNew ? 'new-activity' : ''} ${activity.hasSeen ? 'seen-activity' : ''}`}
                size="small"
                onClick={() => {
                  if (!activity.hasSeen && onMarkAsSeen) {
                    onMarkAsSeen(activity);
                  }
                }}
                style={{ cursor: activity.hasSeen ? 'default' : 'pointer' }}
              >
                <div className="activity-content">
                  <div className="activity-icon">
                    {getActivityIcon(activity.type, activity.status)}
                  </div>
                  <div className="activity-details">
                    <div className="activity-title">
                      {getActivityTitle(activity)}
                      {activity.isNew && !activity.hasSeen && <Badge dot className="new-indicator" />}
                    </div>
                    <div className="activity-description">
                      {getActivityDescription(activity)}
                    </div>
                    <div className="activity-time">
                      {formatTimeAgo(activity.createdAt)}
                    </div>
                  </div>
                  <div className="activity-action">
                    {renderActivityAction(activity)}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivityCard;